import React, { useState } from 'react';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Empty, Tooltip } from 'antd';
import * as QueryString from 'query-string';
import { useLocation } from 'react-router';
import styled from 'styled-components';

import { PaginationBar } from '../../shared/tabs/Glossary/PaginationBar';
import TabToolbar from '../../shared/components/styled/TabToolbar';
import CreateDataProductModal from './CreateDataProductModal';
import { SearchBar } from '../../../search/SearchBar';
import DataProductResult from './DataProductResult';

import { useGetSearchResultsForMultipleQuery } from '../../../../graphql/search.generated';
import { DataProduct, Domain, EntityType } from '../../../../types.generated';
import { DOMAINS_FILTER_NAME } from '../../../search/utils/constants';
import { useTranslation } from '../../../../i18n/useTranslation';
import { useEntityRegistry } from '../../../useEntityRegistry';
import { useEntityData } from '../../shared/EntityContext';
import { scrollToTop } from '../../../shared/searchUtils';
import { ANTD_GRAY } from '../../shared/constants';
import { useRbac } from '../../shared/useRbac';

const ResultsWrapper = styled.div`
    flex: 1;
    overflow: auto;
    background-color: ${ANTD_GRAY[2]};
`;

const StyledLoading = styled(LoadingOutlined)`
    font-size: 32px;
`;

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
`;

const DEFAULT_PAGE_SIZE = 10;

export default function DataProductsTab() {
    const { entityData } = useEntityData();
    const entityRegistry = useEntityRegistry();
    const location = useLocation();
    const params = QueryString.parse(location.search, { arrayFormat: 'comma' });
    const paramsQuery = (params?.query as string) || undefined;
    const [query, setQuery] = useState<string | undefined>(paramsQuery);
    const [page, setPage] = useState(1);
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [editedDataProducts, setEditedDataProducts] = useState<DataProduct[]>([]);
    const [deletedDataProductUrns, setDeletedDataProductUrns] = useState<string[]>([]);

    const { t } = useTranslation();
    const { canCreateDataProduct } = useRbac();

    const canCreate = canCreateDataProduct();
    const createDisabledTooltip = !canCreate ? t('UNAUTHORISED_TOOLTIP') : undefined;

    const start = (page - 1) * DEFAULT_PAGE_SIZE;
    const domainUrn = entityData?.urn || '';

    // Helper function to extract data products
    function extractDataProducts(data): DataProduct[] {
        return (
            data?.searchAcrossEntities?.searchResults
                ?.map((r) => r.entity)
                ?.filter((entity): entity is DataProduct => entity.__typename === 'DataProduct') || []
        );
    }

    // Helper function to deduplicate by `urn`
    function deduplicateByUrn(dataProducts: DataProduct[]): DataProduct[] {
        return Array.from(new Map(dataProducts.map((product) => [product.urn, product])).values());
    }

    const { data, loading, refetch } = useGetSearchResultsForMultipleQuery({
        skip: !domainUrn,
        variables: {
            input: {
                types: [EntityType.DataProduct],
                query: query || '',
                start,
                count: DEFAULT_PAGE_SIZE,
                orFilters: [{ and: [{ field: DOMAINS_FILTER_NAME, values: [domainUrn] }] }],
                searchFlags: { skipCache: true },
            },
        },
    });

    const totalResults = data?.searchAcrossEntities?.total || 0;

    const searchResults = extractDataProducts(data);

    // Use a Map to remove duplicates based on the product urn
    const dataProducts = deduplicateByUrn(searchResults);

    let displayedDataProducts = dataProducts
        .map(
            (dataProduct) =>
                editedDataProducts.find((editedDataProduct) => editedDataProduct.urn === dataProduct.urn) ||
                dataProduct,
        )
        .filter((dataProduct) => !deletedDataProductUrns.includes(dataProduct.urn));

    const onChangePage = (newPage: number) => {
        scrollToTop();
        setPage(newPage);
    };

    function onCreateDataProduct() {
        refetch().then((result) => {
            const newSearchResults = extractDataProducts(result.data);

            const combinedDataProductsTemp = [...newSearchResults];

            // Merge and deduplicate fetched data products
            const dataProductsTemp = deduplicateByUrn(combinedDataProductsTemp);

            const updatedDisplayedDataProducts = dataProductsTemp
                .map(
                    (dataProduct) =>
                        editedDataProducts.find((editedDataProduct) => editedDataProduct.urn === dataProduct.urn) ||
                        dataProduct,
                )
                .filter((dataProduct) => !deletedDataProductUrns.includes(dataProduct.urn));

            displayedDataProducts = updatedDisplayedDataProducts;
        });
    }

    function onUpdateDataProduct(dataProduct: DataProduct) {
        setEditedDataProducts([dataProduct, ...editedDataProducts]);
    }

    return (
        <>
            <TabToolbar>
                <Tooltip title={createDisabledTooltip} overlayClassName="f-tooltip-white">
                    <span>
                        <Button type="text" onClick={() => setIsCreateModalVisible(true)} disabled={!canCreate}>
                            <PlusOutlined /> {t('New Data Product')}
                        </Button>
                    </span>
                </Tooltip>
                <SearchBar
                    initialQuery={query || ''}
                    placeholderText={t('Search data products')}
                    suggestions={[]}
                    style={{
                        maxWidth: 220,
                        padding: 0,
                    }}
                    inputStyle={{
                        height: 32,
                        fontSize: 14,
                    }}
                    onSearch={() => null}
                    onQueryChange={(q) => setQuery(q && q.length > 0 ? q : undefined)}
                    entityRegistry={entityRegistry}
                    hideRecommendations
                />
            </TabToolbar>
            <ResultsWrapper>
                {!loading && !displayedDataProducts.length && (
                    <Empty
                        description={t('No Data Products')}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        style={{ color: ANTD_GRAY[7] }}
                    />
                )}
                {loading && (
                    <LoadingWrapper>
                        <StyledLoading />
                    </LoadingWrapper>
                )}
                {!loading &&
                    displayedDataProducts.map((dataProduct) => (
                        <DataProductResult
                            key={dataProduct.urn}
                            refetch={refetch}
                            dataProduct={dataProduct as DataProduct}
                            onUpdateDataProduct={onUpdateDataProduct}
                            setDeletedDataProductUrns={setDeletedDataProductUrns}
                        />
                    ))}
            </ResultsWrapper>
            <PaginationBar
                page={page}
                loading={loading}
                pageSizeOptions={[10]}
                totalCount={totalResults}
                onShowSizeChange={() => {}}
                onChangePage={onChangePage}
                pageSize={DEFAULT_PAGE_SIZE}
            />
            {isCreateModalVisible && (
                <CreateDataProductModal
                    domain={entityData as Domain}
                    onCreateDataProduct={onCreateDataProduct}
                    onClose={() => setIsCreateModalVisible(false)}
                />
            )}
        </>
    );
}
